import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IOnboardingAntwoord, IOnboardingMe } from '../interfaces/onboarding.interface';
import { NO_ERROR_TOKEN } from '../interceptors/error.interceptor';
import { AuthService } from './auth.service';
import { onboardingBeantwoord } from '../helpers/onboarding';

/**
 * De onboarding van de ingelogde gebruiker (docs/ONBOARDING.md): het
 * welkomstvenster, de kaart "Getting started" en wat er al klaar is.
 *
 * Praat met siq_auth, net als de andere accountaanroepen
 * (AuthService: `${environment.authUrl}/Account/...`). De staat komt één keer
 * per sessie binnen, vanuit de ingelogde schil (UserLayoutComponent), en
 * daarna houdt elke PUT hem bij: de server geeft steeds de hele staat terug.
 */
@Injectable({
  providedIn: 'root'
})
export class OnboardingService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  private readonly basis = `${environment.authUrl}/Onboarding/Me`;

  /** De laatst bekende staat; null zolang hij niet geladen is (of de dienst er niet is). */
  public readonly staat$ = new BehaviorSubject<IOnboardingMe | null>(null);

  /** Staat het welkomstvenster open? */
  public readonly welkomOpen = signal(false);

  private geladen = false;

  public get staat(): IOnboardingMe | null {
    return this.staat$.value;
  }

  /**
   * De staat ophalen, één keer per sessie. Met `tonen` gaat het
   * welkomstvenster open. Mislukt het (dienst nog niet uitgerold, geen
   * netwerk), dan gebeurt er niets: de onboarding is een extraatje en mag
   * het werk niet in de weg zitten. Daarom ook zonder foutmelding.
   * Uitloggen en wisselen van omgeving herladen de pagina, dus daarna begint
   * dit vanzelf opnieuw.
   */
  public laad(): void {
    if (this.geladen || !this.auth.token) {
      return;
    }
    this.geladen = true;
    this.http.get<IOnboardingMe>(this.basis, { context: this.stil() }).pipe(
      catchError(() => {
        // Volgende keer (bijv. na opnieuw inloggen) mag hij het nog eens proberen.
        this.geladen = false;
        return of(null);
      }),
    ).subscribe(staat => {
      if (!staat) {
        return;
      }
      this.staat$.next(staat);
      // Een super-admin die via "Open als club" binnenkomt, is geen nieuwe
      // gebruiker van die club; hem geen welkomstvenster voorschotelen.
      // Wie als een gebruiker meekijkt evenmin: dat welkom is van de gebruiker.
      if (staat.tonen && !this.auth.user?.impersonated_by && !this.auth.user?.viewed_by) {
        this.welkomOpen.set(true);
      }
    });
  }

  /** PUT /Onboarding/Me: de antwoorden (of "overgeslagen"). */
  public bewaar(antwoord: IOnboardingAntwoord): Observable<IOnboardingMe> {
    return this.http.put<IOnboardingMe>(this.basis, antwoord).pipe(tap(staat => this.zet(staat)));
  }

  /** PUT /Onboarding/Me/Klaar/{workflow}: na de laatste stap van een rondleiding. */
  public klaar(workflow: string): Observable<IOnboardingMe> {
    return this.http.put<IOnboardingMe>(`${this.basis}/Klaar/${encodeURIComponent(workflow)}`, {}, { context: this.stil() })
      .pipe(tap(staat => this.zet(staat)));
  }

  /** PUT /Onboarding/Me/Verborgen: de kaart op de startpagina weg of terug. */
  public verberg(verborgen: boolean): Observable<IOnboardingMe> {
    return this.http.put<IOnboardingMe>(`${this.basis}/Verborgen`, { verborgen }).pipe(tap(staat => this.zet(staat)));
  }

  /** PUT /Onboarding/Me/Opnieuw: status terug naar `nieuw`; `klaar` blijft. */
  public opnieuw(): Observable<IOnboardingMe> {
    return this.http.put<IOnboardingMe>(`${this.basis}/Opnieuw`, {}).pipe(tap(staat => this.zet(staat)));
  }

  /**
   * Help -> "Getting started". Wie de vragen beantwoord heeft, krijgt de
   * kaart terug op de startpagina; wie ze nooit beantwoordde (nieuw,
   * overgeslagen of van vóór de onboarding) begint de introductie opnieuw.
   */
  public openAanDeSlag(): void {
    const staat$ = this.staat ? of(this.staat) : this.http.get<IOnboardingMe>(this.basis).pipe(tap(s => this.zet(s)));
    staat$.subscribe(staat => {
      if (onboardingBeantwoord(staat)) {
        const terug = staat.verborgen ? this.verberg(false) : of(staat);
        terug.subscribe(() => void this.router.navigateByUrl('/'));
      } else {
        this.opnieuw().pipe(map(() => true), catchError(() => of(false))).subscribe(gelukt => {
          if (gelukt) {
            this.welkomOpen.set(true);
          }
        });
      }
    });
  }

  private zet(staat: IOnboardingMe): void {
    if (staat) {
      this.staat$.next(staat);
    }
  }

  /** Geen toast bij een fout: de onboarding mag nooit in de weg zitten. */
  private stil(): HttpContext {
    return new HttpContext().set(NO_ERROR_TOKEN, true);
  }
}
